import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowLeft, Upload, FileText, Target, Zap, Download, CheckCircle, Shield, Award, Clock, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

const quickSteps = [
  { icon: Upload, title: "Upload Resume", description: "Drop in your resume or select from library" },
  { icon: FileText, title: "Add the Job", description: "Paste job posting or enter URL" },
  { icon: Target, title: "Match Score", description: "See your 0-100 rating instantly" },
  { icon: Zap, title: "AI Keywords", description: "Target the right skills & qualifications" },
  { icon: CheckCircle, title: "One-Click Tailor", description: "Get customized, ATS-friendly resume" },
  { icon: Shield, title: "Fill Gaps", description: "Honest relevancy statements added" },
  { icon: Download, title: "Download & Apply", description: "Save in Word or PDF format" }
];

const detailedSteps = [
  {
    title: "Upload Your Resume & Pick a Design",
    content: "Upload, paste, or select an existing resume from your TailorWiz library. Choose from ATS-friendly templates in Dynamic, Traditional, or Modern styles—or keep your original layout."
  },
  {
    title: "Select the Job Description", 
    content: "Paste the job description, enter the job post URL, or choose one from your saved list."
  },
  {
    title: "Get Your Job Match Score",
    content: "With one click, TailorWiz compares your resume to the job description and produces a score between 0–100, showing how closely you match."
  },
  {
    title: "See the Key Skills & Keywords",
    content: "AI pinpoints the most important skills, qualifications, and company values—organized into clear lists."
  },
  {
    title: "Confirm Relevant Requirements",
    content: "Drag and drop to keep only the requirements you meet. Edit or add as needed."
  },
  {
    title: "Get a Qualification Check",
    content: "See if you're fully qualified, partially qualified, or missing requirements—so you can decide whether to apply."
  },
  {
    title: "Generate a Tailored Resume",
    content: "Your resume is rebuilt around the target job, with a new summary, skills section, work history, and education that align directly to the role."
  },
  {
    title: "Address Gaps Honestly",
    content: "We include statements showing your ability and willingness to learn any missing skills."
  },
  {
    title: "Keep It Natural",
    content: "Your resume stays authentic and human while still being ATS-optimized."
  },
  {
    title: "Choose Your Layout",
    content: "Keep your existing design or apply one of our proven ATS-friendly templates."
  },
  {
    title: "Edit & Refine",
    content: "Make instant edits in our Word-style editor. Remove irrelevant information so hiring managers focus only on what matters."
  },
  {
    title: "Check Your Final Score",
    content: "Review your updated Job Match Score—optimized to 90% or higher."
  },
  {
    title: "Download & Apply",
    content: "Save in Word or PDF and apply immediately. The process can take as little as 60 seconds."
  }
];

export default function HowItWorks() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-primary to-primary-dark rounded-lg flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="text-xl font-bold text-foreground">How It Works</span>
          </div>
          <div></div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary/10 to-primary-dark/10 py-20 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <Badge variant="outline" className="mb-6 text-primary border-primary">
            STEP-BY-STEP PROCESS
          </Badge>
          <h1 className="text-5xl md:text-6xl font-bold text-foreground mb-6">
            Tailor Your Resume for Any Job in <span className="text-primary">60 Seconds</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8 leading-relaxed">
            Land more interviews in less time. From upload to application in under a minute, TailorWiz transforms your resume 
            to perfectly match any job posting—optimized for both humans and ATS scanners.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              onClick={() => navigate('/pricing')}
              className="bg-primary hover:bg-primary-dark text-white font-bold px-8 py-4 text-lg"
            >
              Try TailorWiz Now
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              onClick={() => navigate('/features')}
              className="border-2 border-primary text-primary hover:bg-primary hover:text-white px-8 py-4 text-lg"
            >
              See All Features
            </Button>
          </div>
        </div>
      </section>

      {/* Quick Steps Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4">Quick Steps Overview</h2>
            <p className="text-lg text-muted-foreground">Simple process, powerful results</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-6">
            {quickSteps.map((step, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-all duration-300 hover:scale-105">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <step.icon className="w-6 h-6 text-primary" />
                  </div>
                  <div className="text-sm font-semibold text-primary mb-1">{index + 1}</div>
                  <h3 className="font-bold text-sm mb-2">{step.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{step.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Full Steps Section */}
      <section className="py-16 px-4 bg-muted/30">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4">Full Step-by-Step Process</h2>
            <p className="text-lg text-muted-foreground">Detailed breakdown of how TailorWiz works</p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {detailedSteps.map((step, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="border border-border rounded-lg bg-card">
                <AccordionTrigger className="px-6 py-4 hover:no-underline">
                  <div className="flex items-center gap-4 text-left">
                    <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {index + 1}
                    </div>
                    <span className="font-semibold">{step.title}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4">
                  <div className="ml-12 text-muted-foreground leading-relaxed">
                    {step.content}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Why This Works Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-green-50 to-blue-50">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl font-bold text-foreground mb-6">Why This Works</h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto mb-12 leading-relaxed">
            Most resumes fail because they don't match the exact language and priorities of the job posting. 
            TailorWiz fixes that by doing the hard work for you—analyzing, aligning, and optimizing every section 
            so you stand out to both recruiters and applicant tracking systems. You walk away with a resume that's 
            targeted, relevant, and interview-ready.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <Card className="border-2 border-green-200 bg-green-50">
              <CardContent className="p-6 text-center">
                <Shield className="w-8 h-8 text-green-600 mx-auto mb-3" />
                <h3 className="font-bold text-lg text-green-800 mb-2">ATS-Friendly</h3>
                <p className="text-green-700 text-sm">Guaranteed to pass applicant tracking systems</p>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-blue-200 bg-blue-50">
              <CardContent className="p-6 text-center">
                <Award className="w-8 h-8 text-blue-600 mx-auto mb-3" />
                <h3 className="font-bold text-lg text-blue-800 mb-2">Interview Guarantee</h3>
                <p className="text-blue-700 text-sm">90%+ match scores lead to more interviews</p>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-purple-200 bg-purple-50">
              <CardContent className="p-6 text-center">
                <Clock className="w-8 h-8 text-purple-600 mx-auto mb-3" />
                <h3 className="font-bold text-lg text-purple-800 mb-2">Lightning Fast</h3>
                <p className="text-purple-700 text-sm">Complete process in under 60 seconds</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-primary to-primary-dark text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Land More Interviews?</h2>
          <p className="text-xl mb-8 opacity-90">
            Join thousands of professionals who've transformed their job search with TailorWiz
          </p>
          <Button 
            size="lg" 
            onClick={() => navigate('/pricing')}
            className="bg-white text-primary hover:bg-gray-50 font-bold px-8 py-4 text-lg"
          >
            Start Tailoring My Resume
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
          <p className="text-sm mt-4 opacity-75">No credit card needed</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-card/30 py-8">
        <div className="container mx-auto px-4 text-center">
          <p className="text-muted-foreground">
            © 2025 TailorWiz. All rights reserved. Transform your job search today.
          </p>
        </div>
      </footer>
    </div>
  );
}